import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useApi } from '../../api/hooks'
import { useAssetTypes } from '../../hooks/useQueries'
import { componentErrorMessage, useAssetComponents, useDetachAssembly, useSetAssembly } from '../../hooks/useComponents'

/**
 * **Part of** for an equipment drawn on a plan (spec 2026-10-27-17): the
 * equipment it is a component of, picked among the site's equipment of the
 * types it is usually part of (`assembly_type_codes`), saved through
 * `PUT /assets/{uid}/assembly`. The component stays where it is drawn: the
 * edge never moves it. Renders nothing for a type that is no usual component
 * and has no assembly. Networks keep NetworkAssemblyControl.
 */
export function AssemblyControl({ org, assetUid }: { org: string; assetUid: string }) {
  const { t } = useTranslation('assets')
  const { request } = useApi()
  const { data: view } = useAssetComponents(org, assetUid)
  const { data: assetTypes = [] } = useAssetTypes(org)
  const setAssembly = useSetAssembly(org)
  const detach = useDetachAssembly(org)
  const [error, setError] = useState<string | null>(null)
  const codes = view?.assembly_type_codes ?? []
  const siteUid = view?.site_uid ?? undefined
  const typeUids = (assetTypes as Array<{ uid: string; code: string }>)
    .filter((at) => codes.includes(at.code))
    .map((at) => at.uid)

  const { data: candidates = [] } = useQuery({
    queryKey: ['assembly-candidates', org, siteUid ?? '', typeUids.join(',')],
    enabled: !!view && typeUids.length > 0,
    queryFn: async () => {
      const query: Record<string, string> = { limit: '200', sort: 'name' }
      if (siteUid) query.ancestor_uid = siteUid
      const res = await request('/api/v1/organizations/{orgCode}/assets', 'GET', {
        params: { path: { orgCode: org }, query },
      })
      const rows = ((res as { data?: Array<{ uid: string; name?: string | null; asset_type_uid?: string | null }> }).data) ?? []
      return rows.filter((a) => a.uid !== assetUid && !!a.asset_type_uid && typeUids.includes(a.asset_type_uid))
    },
  })

  if (!view || (!view.assembly && codes.length === 0)) return null
  const options = [...candidates]
  if (view.assembly && !options.some((c) => c.uid === view.assembly?.uid)) {
    options.push({ uid: view.assembly.uid, name: view.assembly.name, asset_type_uid: null })
  }
  const busy = setAssembly.isPending || detach.isPending
  const onSelect = async (uid: string) => {
    setError(null)
    try {
      if (uid) await setAssembly.mutateAsync(assetUid, uid)
      else await detach.mutateAsync(assetUid)
    } catch (err) {
      setError(componentErrorMessage(err, t))
    }
  }

  return (
    <div className="space-y-1 py-1" data-testid="plan-editor-assembly-field">
      <div className="flex items-center gap-2">
        <span className="w-28 shrink-0 text-xs text-muted-foreground">{t('planEditor.part_of')}</span>
        <select
          data-testid="plan-editor-assembly-select"
          aria-label={t('planEditor.part_of')}
          className="h-7 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground"
          value={view.assembly?.uid ?? ''}
          disabled={busy}
          onChange={(e) => { void onSelect(e.target.value) }}
        >
          <option value="">{t('planEditor.part_of_none')}</option>
          {options.map((c) => (
            <option key={c.uid} value={c.uid}>{c.name || c.uid}</option>
          ))}
        </select>
      </div>
      {view.assembly && (
        <Link
          to="/$org/assets/$assetUid"
          params={{ org, assetUid: view.assembly.uid }}
          className="ml-[7.5rem] block text-[11px] text-primary hover:underline"
          data-testid="plan-editor-assembly-link"
        >
          {view.assembly.name}
        </Link>
      )}
      {error && (
        <p className="text-[11px] text-destructive" data-testid="plan-editor-assembly-error">{error}</p>
      )}
    </div>
  )
}
